import type { Tour } from '../tour-types';
import { SERVER_SIDE_STAFF_ONLY, canUseServerSide } from 'definitions/server-side-access';

/**
 * Server-side is only offered to staff while server-side is (PER-178), so until it launches
 * the step names the methods everyone can choose. Staff know the fourth.
 */
const TRACKING_METHODS_COPY = SERVER_SIDE_STAFF_ONLY
  ? 'by Pertento, GA4 or Matomo'
  : 'by Pertento, GA4, Matomo, or Server-side, where only your backend reports them';

/**
 * The websites list. Every step but the first points at the first row of the table, so the
 * tour waits for at least one website (the empty state holds it back). Snippet, goals, server
 * keys and settings open as dialogs; the steps point at the row buttons that open them.
 */
export const websitesTour: Tour = {
  key: 'websites.list',
  version: 3,
  routes: ['/websites/list'],
  steps: [
    {
      anchor: 'websites-create',
      placement: 'left',
      requires: 'websites:manage',
      title: 'Add a website',
      body: 'Each site you want to test is a website here. Add its address, then put its snippet in its pages.',
    },
    {
      anchor: 'websites-select',
      title: 'Work on this site',
      body: 'Click the ID to make this the website in the header, so every screen shows its experiments and data.',
    },
    {
      anchor: 'websites-scripts',
      title: 'Implementation',
      body: "The script that goes in the site's pages so experiments and surveys can run there. Copy it from here.",
    },
    {
      anchor: 'websites-container',
      title: 'Server container',
      body: 'If you send Google Analytics hits through a server-side tag container, its address goes here. Sites measured by Pertento do not use it.',
    },
    {
      anchor: 'websites-tracking',
      title: 'Tracking',
      body: `How conversions on this site are measured: ${TRACKING_METHODS_COPY}. A linked Google property also shows whether Pertento can edit it, and Recheck asks Google again.`,
    },
    {
      anchor: 'websites-goals',
      requires: 'websites:manage',
      title: 'Event goals',
      body: 'Sort the events this site reports into primary objectives, which decide a result, and secondary ones shown alongside as evidence.',
    },
    {
      anchor: 'websites-server-keys',
      placement: 'left',
      requires: 'server-keys:manage',
      // 3, after Data feeds (2): a step added later takes the next version, so staff who saw 2
      // still get this one. Staff-only until server-side launches (PER-178), so customers are
      // held at 2 (versionFor).
      since: 3,
      when: canUseServerSide,
      title: 'Server keys',
      body: 'Keys your backend uses with the Pertento SDK to assign visitors and report conversions. Create one per environment and revoke any you no longer use.',
    },
    {
      anchor: 'websites-settings',
      placement: 'left',
      requires: 'websites:manage',
      title: 'Website settings',
      body: 'The currency, the tracking method, the visitor ID, and the default primary objective that experiments are measured on when they do not choose their own.',
    },
    {
      anchor: 'websites-feeds',
      placement: 'left',
      requires: 'websites:manage',
      since: 2,
      title: 'Data feeds',
      body: 'Connect a product, review or stock feed in Settings. Components in the editor can then show its values, such as a rating or a delivery date, matched to the product on each page.',
    },
  ],
};
